import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, Stack, useTheme } from '@mui/material';
import useAuth from 'hooks/useAuth';
import useClinicDocument from 'hooks/useClinicDocument';
import useClinicDocuments from 'hooks/useClinicDocuments';
import useFormBuilder from 'hooks/useFormBuilder';
import useOptions from 'hooks/useOptions';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { store } from 'store';
import { RESET_FIELDS, SET_DOCUMENT_ID } from 'store/actions';
import DialogClose from 'views/components/buttons/DialogClose';
import CenteredLoading from 'views/components/CenteredLoading';
import CheckboxGroup from 'views/components/inputs/CheckBoxGroupOther';
import FormContainer from 'views/components/inputs/FormContainer';
import Select from 'views/components/inputs/Select';
import CreateForm from 'views/components/modals/CreateForm';

const EditAddCustomFormModal = ({ editingFolderId, onClose, documentId, formId }: any) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { showModal } = useModal();
  const { userId } = useAuth();
  const { userForms, userFormsIsLoading, refetch, updateForm, refetchForms } = useFormBuilder({ userId });
  const [isFormBuilderForm, setIsFormBuilderForm] = useState(true);
  const [open, setOpen] = useState(false);

  // type FormValues = Yup.InferType<typeof validationSchema>;
  const { document, createClinicDocumentForm, isLoadingDocument, isUpdatingDocument } = useClinicDocument({
    documentId: documentId ?? editingFolderId
  });

  const form = document.documents.filter((document) => document._id === formId)[0];

  const onFormSubmit = async (values) => {
    const { targetAudience, title, template } = values;
    // console.log('Submitting clinic document');
    // onClose();
    let data;
    // if (formId) {
    // data = await createClinicDocumentForm({ title, targetAudience, template });
    // }
    // setOpen(true);
    // console.log('docuemnt id ', documentId);
    const modal = showModal(CreateForm, {
      open: true,
      onClose: async () => {
        modal.hide();
        store.dispatch({ type: RESET_FIELDS });
        setOpen(false);
        await refetch();
        onClose();
      },
      onCancel: () => {
        modal.hide();
        setOpen(false);
        onClose();
      },
      formId: '',
      isFromTemplate: !isFormBuilderForm,
      isEditing: false,
      isClinicDocuments: true,
      documentId,
      targetAudience
    });

    // enqueueSnackbar(data.message, { variant: 'success' });
    // onClose();
  };

  const initialValues = {
    title: !!formId ? form.form.title : '',
    targetAudience: !!formId ? form.targetAudience : [],
    templateFolder: 'default',
    template: 'Blank'
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {!!formId ? <FormattedMessage defaultMessage="Edit Custom Form" /> : <FormattedMessage defaultMessage="Create Custom Form" />}
      </DialogTitle>
      <DialogClose onClose={onClose} />
      <Divider />
      <DialogContent>
        {isLoadingDocument ? (
          <CenteredLoading />
        ) : (
          <FormContainer defaultValues={initialValues} onSuccess={onFormSubmit}>
            <EditAddFields formId={formId} isFormBuilderForm={isFormBuilderForm} setIsFormBuilderForm={setIsFormBuilderForm} />

            <DialogActions sx={{ mt: 3 }}>
              <Divider />
              <Button variant="contained" type="submit" sx={{ width: 180 }}>
                {formId ? <FormattedMessage defaultMessage="Update" /> : <FormattedMessage defaultMessage="Create" />}
              </Button>
            </DialogActions>
          </FormContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const EditAddFields = ({ formId, isFormBuilderForm, setIsFormBuilderForm }: any) => {
  const theme = useTheme();
  const intl = useIntl();
  const { roles } = useOptions();

  const { watch, setValue } = useFormContext();
  const templateFolder = watch('templateFolder');

  const { documents, isDocumentsLoading } = useClinicDocuments();

  const { customForms, isLoadingCustomForms } = useClinicDocument({ documentId: templateFolder === 'default' ? null : templateFolder });

  const folders = useMemo(() => {
    const tmpFolders: any = [];
    // console.log('Forms are ', documents);
    documents?.documents?.forEach((folder) => {
      // console.log('temp folders length', folder.forms?.length);
      // if (folder.forms?.length) {
      // console.log("tm")
      tmpFolders.push({ label: folder.title, value: folder._id });
      // console.log('temp folders pushed ', tmpFolders);
      // }
    });

    return tmpFolders;
  }, [documents]);

  const templates = useMemo(() => {
    setValue('template', 'Blank');

    if (templateFolder === 'default' || !customForms) {
      return [
        { label: intl.formatMessage({ defaultMessage: 'Blank' }), value: 'Blank' },
        { label: intl.formatMessage({ defaultMessage: 'Default Intake Form' }), value: 'Default Intake Form' }
      ];
    }

    return customForms.forms.map(({ form }) => {
      return { label: intl.locale === 'fr' ? form.titleFrench || form.title : form.title || form.titleFrench, value: form._id };
    });
  }, [setValue, templateFolder, customForms, intl]);

  return (
    <Stack spacing={4}>
      <Box
        sx={{ border: 1, borderColor: '#ddd', borderStyle: 'solid', borderRadius: theme.shape.borderRadius, p: 2, width: 'fit-content' }}
      >
        <CheckboxGroup
          name="targetAudience"
          label={intl.formatMessage({ defaultMessage: 'Target Audience' })}
          options={
            roles
              ?.filter((role: any) => !['NOUS_SUPPORT', 'CLIENT'].includes(role.roleName))
              ?.map((role: any) => ({
                label: intl.locale === 'fr' ? role?.roleNameFrench : role.roleName,
                id: role.roleName
              })) ?? []
          }
          row
        />
      </Box>

      {/* <TextField name="title" label={intl.formatMessage({ defaultMessage: 'Title' })} /> */}
      {/* <RadioGroup name="isForNotes" row onChange={(e) => setIsFormBuilderForm(e.target.value === 'true')}>
        <FormControlLabel
          value={false}
          control={<Radio />}
          checked={!isFormBuilderForm}
          label={<FormattedMessage defaultMessage="Form Templates" />}
        />
        <FormControlLabel
          value={true}
          control={<Radio />}
          checked={isFormBuilderForm}
          label={<FormattedMessage defaultMessage="Form Builder Forms" />}
        />
      </RadioGroup> */}

      {!formId && (
        <Stack direction="row" spacing={2}>
          <Select
            name="templateFolder"
            label={intl.formatMessage({ defaultMessage: 'Template Folder' })}
            options={folders}
            fullWidth
            onChange={(e) => {
              setValue('templateFolder', e.target.value);
              store.dispatch({ type: SET_DOCUMENT_ID, payload: e.target.value });
            }}
          />
          {/* <Select name="template" label={intl.formatMessage({ defaultMessage: 'Template' })} options={templates} fullWidth /> */}
        </Stack>
      )}

      {(isDocumentsLoading || isLoadingCustomForms) && <LinearProgress variant="indeterminate" />}
    </Stack>
  );
};

export default EditAddCustomFormModal;
